import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_URL as API } from '../config/api';

const ResetPassword = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Page-specific translations
  const pageTexts = {
    fr: {
      title: "Réinitialiser votre mot de passe",
      subtitle: "Créez un nouveau mot de passe pour votre compte",
      newPassword: "Nouveau mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      resetButton: "Réinitialiser le mot de passe",
      resetting: "Réinitialisation...",
      successTitle: "Mot de passe réinitialisé !",
      successMessage: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.",
      loginButton: "Se connecter",
      invalidToken: "Lien invalide ou expiré",
      invalidTokenMessage: "Ce lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.",
      requestNewLink: "Demander un nouveau lien",
      verifyingToken: "Vérification du lien...",
      passwordMinLength: "Le mot de passe doit contenir au moins 6 caractères",
      passwordsNotMatch: "Les mots de passe ne correspondent pas",
      fillAllFields: "Veuillez remplir tous les champs",
      resetError: "Erreur lors de la réinitialisation"
    },
    en: {
      title: "Reset your password",
      subtitle: "Create a new password for your account",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      resetButton: "Reset password",
      resetting: "Resetting...",
      successTitle: "Password reset!",
      successMessage: "Your password has been successfully reset. You can now log in.",
      loginButton: "Log in",
      invalidToken: "Invalid or expired link",
      invalidTokenMessage: "This reset link is invalid or has expired. Please request a new link.",
      requestNewLink: "Request new link",
      verifyingToken: "Verifying link...",
      passwordMinLength: "Password must be at least 6 characters",
      passwordsNotMatch: "Passwords do not match",
      fillAllFields: "Please fill in all fields",
      resetError: "Error during reset"
    },
    ht: {
      title: "Reyinisyalize modpas ou",
      subtitle: "Kreye yon nouvo modpas pou kont ou",
      newPassword: "Nouvo modpas",
      confirmPassword: "Konfime modpas",
      resetButton: "Reyinisyalize modpas",
      resetting: "Reyinisyalizasyon...",
      successTitle: "Modpas reyinisyalize!",
      successMessage: "Modpas ou reyinisyalize avèk siksè. Ou kapab konekte kounye a.",
      loginButton: "Konekte",
      invalidToken: "Lyen envalid oswa ekspire",
      invalidTokenMessage: "Lyen reyinisyalizasyon sa a envalid oswa ekspire. Tanpri mande yon nouvo lyen.",
      requestNewLink: "Mande nouvo lyen",
      verifyingToken: "Verifikasyon lyen...",
      passwordMinLength: "Modpas la dwe gen omwen 6 karaktè",
      passwordsNotMatch: "Modpas yo pa matche",
      fillAllFields: "Tanpri ranpli tout chan yo",
      resetError: "Erè pandan reyinisyalizasyon"
    },
    es: {
      title: "Restablecer su contraseña",
      subtitle: "Cree una nueva contraseña para su cuenta",
      newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar contraseña",
      resetButton: "Restablecer contraseña",
      resetting: "Restableciendo...",
      successTitle: "¡Contraseña restablecida!",
      successMessage: "Su contraseña ha sido restablecida con éxito. Ahora puede iniciar sesión.",
      loginButton: "Iniciar sesión",
      invalidToken: "Enlace inválido o expirado",
      invalidTokenMessage: "Este enlace de restablecimiento es inválido o ha expirado. Por favor solicite un nuevo enlace.",
      requestNewLink: "Solicitar nuevo enlace",
      verifyingToken: "Verificando enlace...",
      passwordMinLength: "La contraseña debe tener al menos 6 caracteres",
      passwordsNotMatch: "Las contraseñas no coinciden",
      fillAllFields: "Por favor complete todos los campos",
      resetError: "Error durante el restablecimiento"
    },
    pt: {
      title: "Redefinir sua senha",
      subtitle: "Crie uma nova senha para sua conta",
      newPassword: "Nova senha",
      confirmPassword: "Confirmar senha",
      resetButton: "Redefinir senha",
      resetting: "Redefinindo...",
      successTitle: "Senha redefinida!",
      successMessage: "Sua senha foi redefinida com sucesso. Agora você pode fazer login.",
      loginButton: "Fazer login",
      invalidToken: "Link inválido ou expirado",
      invalidTokenMessage: "Este link de redefinição é inválido ou expirou. Por favor solicite um novo link.",
      requestNewLink: "Solicitar novo link",
      verifyingToken: "Verificando link...",
      passwordMinLength: "A senha deve ter pelo menos 6 caracteres",
      passwordsNotMatch: "As senhas não coincidem",
      fillAllFields: "Por favor preencha todos os campos",
      resetError: "Erro durante a redefinição"
    }
  };

  const pt = pageTexts[i18n.language] || pageTexts.fr;

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setEmail(response.data.email);
        }
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error(pt.fillAllFields);
      return;
    }

    if (newPassword.length < 6) {
      toast.error(pt.passwordMinLength);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(pt.passwordsNotMatch);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });

      setSuccess(true);
    } catch (error) {
      console.error('Reset error:', error);
      toast.error(error.response?.data?.detail || pt.resetError);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-accent/10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">{pt.verifyingToken}</p>
        </motion.div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-accent/10 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{pt.invalidToken}</h1>
          <p className="text-gray-600 mb-6">{pt.invalidTokenMessage}</p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-primary hover:bg-primary/90 text-white w-full"
          >
            {pt.requestNewLink}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-accent/10 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          </motion.div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{pt.successTitle}</h1>
          <p className="text-gray-600 mb-6">{pt.successMessage}</p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-primary hover:bg-primary/90 text-white w-full"
          >
            {pt.loginButton}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-accent/10 pt-24 pb-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{pt.title}</h1>
          <p className="text-gray-600 mt-2">{pt.subtitle}</p>
          {email && (
            <p className="text-sm text-primary mt-2 bg-primary/10 py-1 px-3 rounded-full inline-block">
              {email}
            </p>
          )}
        </div>

        <form onSubmit={handleReset} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="newPassword">{pt.newPassword}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-10"
                placeholder="••••••••"
                minLength={6}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{pt.confirmPassword}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10"
                placeholder="••••••••"
                minLength={6}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-white py-3"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {pt.resetting}
              </>
            ) : (
              pt.resetButton
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
